import api from "../../../utils/api";

// ---------------------------------------------------------------------------
// Releases y despliegues (#997 / #998). `GET /workHoursReleases` devuelve la
// versión actual por plataforma × entorno × componente, los releases del rango
// y sus métricas. Las respuestas vienen como `{ meta, data }`.
// ---------------------------------------------------------------------------

export type ReleaseEnvironment = "qa" | "prod";
export type ReleaseComponent = "frontend" | "backend";
export type ReleaseMethod = "manual" | "github_actions" | "automation";

export interface ReleasePerson {
  login: string | null;
  email: string | null;
  name: string | null;
}

export interface ReleasePr {
  number: number;
  title: string | null;
  url: string | null;
  author?: string | null;
  mergedAt?: string | null;
}

export interface ReleaseCommit {
  sha: string;
  message: string;
  author: string | null;
  date: string | null;
  url: string | null;
  repo?: string;
}

export interface Release {
  id: string;
  platform: string;
  repo: string;
  environment: ReleaseEnvironment;
  component: ReleaseComponent;
  version: string | null;
  sha: string;
  deployedAt: string;
  deployedBy: ReleasePerson;
  method: ReleaseMethod;
  target: string | null;
  runUrl: string | null;
  notes: string | null;
  notesSource: "manual" | "pull_requests" | null;
  sourcePr: ReleasePr | null;
  commits: ReleaseCommit[];
  pullRequests: (ReleasePr & { repo?: string })[];
  previousVersion: string | null;
  previousSha: string | null;
  enrichment?: {
    status: "done" | "partial" | "skipped" | "error";
    reason?: string;
    firstRelease?: boolean;
    commitsTruncated?: { shown: number; total: number };
  };
}

export type ReleaseBrief = Pick<Release, "id" | "version" | "sha" | "deployedAt" | "deployedBy" | "method" | "sourcePr" | "target">;

export interface CurrentRow {
  platform: string;
  repo: string | null;
  qa: Record<ReleaseComponent, ReleaseBrief | null>;
  prod: Record<ReleaseComponent, ReleaseBrief | null>;
}

export interface ReleaseMetrics {
  total: number;
  byEnvironment: Record<ReleaseEnvironment, number>;
  byMethod: Record<ReleaseMethod, number>;
  manualShare: number;
  avgDaysBetweenProd: number | null;
  byPerson: { key: string; name: string; count: number; prod: number }[];
  byPlatform: { platform: string; qa: number; prod: number }[];
  commitCount: number;
  pullRequestCount: number;
  commits: ReleaseCommit[];
  pullRequests: (ReleasePr & { repo?: string })[];
}

export interface ReleasesResponse {
  from: string | null;
  to: string | null;
  current: CurrentRow[];
  metrics: ReleaseMetrics;
  releases: Release[];
  platforms: { id: string; repo: string }[];
  githubConfigured: boolean;
  truncated: boolean;
}

export async function fetchReleases(params: { from?: string; to?: string; platform?: string; environment?: string }): Promise<ReleasesResponse> {
  const res = await api.get("/workHoursReleases", { params });
  return res?.data?.data as ReleasesResponse;
}

/** Enlace a un commit o PR en GitHub. */
export const commitLink = (repo: string, sha: string) => `https://github.com/${repo}/commit/${sha}`;
export const shortSha = (sha: string | null | undefined) => (sha ? sha.slice(0, 7) : "—");
